import clsx from "clsx";
import * as React from "react";
import { ScrollArea, type ScrollAreaProps } from "../scroll-area/ScrollArea.js";

type TableStripedType = boolean | "odd" | "even";
type TableContextType = {
  fixedLayout?: boolean;
  striped?: TableStripedType;
  highlightOnHover?: boolean;
  withTableBorder?: boolean;
};

interface TableScrollContainerProps extends ScrollAreaProps {
  minWidth?: number;
}

interface TabelCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  minWidth?: number;
}

const TableContext = React.createContext<TableContextType>({
  fixedLayout: false,
  striped: false,
  highlightOnHover: false,
  withTableBorder: false,
});

const useTableContext = () => {
  const context = React.useContext(TableContext);
  if (context === undefined) {
    throw new Error(
      "useTableContext must be used within a TableContext.Provider",
    );
  }
  return context;
};

// TableProps extends the basic HTML attributes for a table element
interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  fixedLayout?: boolean;
  fontSize?: "xs" | "sm" | "md" | "lg";
  striped?: TableStripedType;
  highlightOnHover?: boolean;
  withTableBorder?: boolean;
}

interface TableComponent
  extends React.ForwardRefExoticComponent<
    TableProps & React.RefAttributes<HTMLTableElement>
  > {
  Head: typeof TableHead;
  Body: typeof TableBody;
  Row: typeof TableRow;
  Cell: typeof TableCell;
  Th: typeof TableTh;
  ScrollContainer: typeof TableScrollContainer;
}

// Table component
const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      children,
      className,
      fixedLayout = false,
      fontSize = "sm",
      highlightOnHover,
      withTableBorder,
      striped,
      ...props
    },
    ref,
  ) => {
    return (
      <TableContext.Provider
        value={{
          fixedLayout,
          striped: striped === true ? "odd" : striped || undefined,
          highlightOnHover,
        }}
      >
        <table
          ref={ref}
          data-fixed-layout={fixedLayout}
          data-font-size={fontSize}
          data-border={withTableBorder}
          className={clsx(" ox-Table", className)}
          {...props}
        >
          {children}
        </table>
      </TableContext.Provider>
    );
  },
) as TableComponent;

// TableHead component
const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ children, className, ...props }, ref) => {
  const { fixedLayout } = useTableContext();
  return (
    <thead
      ref={ref}
      data-fixed-layout={fixedLayout}
      className={clsx("ox-reset ox-TableHead", className)}
      {...props}
    >
      {children}
    </thead>
  );
});

// TableBody component
const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ children, className, ...props }, ref) => {
  const { fixedLayout } = useTableContext();
  return (
    <tbody
      ref={ref}
      data-fixed-layout={fixedLayout}
      className={clsx("ox-reset ox-TableBody", className)}
      {...props}
    >
      {children}
    </tbody>
  );
});

// TableRow component
const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ children, className, ...props }, ref) => {
  const { fixedLayout, striped, highlightOnHover } = useTableContext();
  return (
    <tr
      ref={ref}
      data-fixed-layout={fixedLayout}
      data-striped={striped}
      data-hover={highlightOnHover}
      className={clsx("ox-reset ox-TableRow", className)}
      {...props}
    >
      {children}
    </tr>
  );
});

// TableCell component
const TableCell = React.forwardRef<HTMLTableCellElement, TabelCellProps>(
  ({ children, className, minWidth, style, ...props }, ref) => {
    const { fixedLayout } = useTableContext();

    const defaultStyles: React.CSSProperties = {
      minWidth: minWidth ?? undefined,
    };

    return (
      <td
        ref={ref}
        data-fixed-layout={fixedLayout}
        className={clsx("ox-reset ox-TableCell", className)}
        style={{
          ...defaultStyles,
          ...style,
        }}
        {...props}
      >
        {children}
      </td>
    );
  },
);

const TableTh = React.forwardRef<
  HTMLTableCellElement,
  React.HTMLAttributes<HTMLTableCellElement>
>(({ children, className, ...props }, ref) => {
  const { fixedLayout } = useTableContext();
  return (
    <th
      ref={ref}
      data-fixed-layout={fixedLayout}
      className={clsx("ox-reset ox-TableTh", className)}
      {...props}
    >
      {children}
    </th>
  );
});

// TableScrollContainer component
const TableScrollContainer = React.forwardRef<
  React.ElementRef<typeof ScrollArea>,
  TableScrollContainerProps
>(({ className, minWidth, style, ...props }, ref) => {
  const defaultStyles: React.CSSProperties = {
    minWidth: minWidth ?? "100%",
    overflowX: "auto",
  };
  return (
    <ScrollArea
      ref={ref}
      className={clsx("ox-reset ox-TableScrollContainer", className)}
      style={{
        ...defaultStyles,
        ...style,
      }}
      {...props}
    />
  );
});

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.Th = TableTh;
Table.ScrollContainer = TableScrollContainer;

Table.Head.displayName = "Table.Head";
Table.Body.displayName = "Table.Body";
Table.Row.displayName = "Table.Row";
Table.Th.displayName = "Table.Th";
Table.displayName = "Table";
Table.ScrollContainer.displayName = "Table.ScrollContainer";
export { Table };
export type { TableProps };
